/**
 * Fluevog footer email signup.
 * Writes the address to Shopify (customer form on /contact) over AJAX, then
 * opens the Attentive sign-up unit pre-filled with that address so the visitor
 * finishes there (SMS step + consent copy, both owned by the Attentive unit).
 */
(function () {
  // Attentive's own sample uses 760px to pick between mobile/desktop units.
  const DESKTOP_MIN_WIDTH = 760;
  // The tag may still be loading when the visitor submits, so wait it out.
  const ATTENTIVE_POLL_MS = 250;
  const ATTENTIVE_TIMEOUT_MS = 5000;
  // How long to give the sign-up unit to paint after a trigger call.
  const CREATIVE_POLL_MS = 150;
  const CREATIVE_TIMEOUT_MS = 1500;
  // Attentive keeps #attentive_creative / #attentive_overlay on the page at a
  // small idle size (~166x60), so an open unit is judged by area, not presence.
  const CREATIVE_SELECTOR =
    '#attentive_creative, [id^="attentive_creative"], #attentive_overlay, iframe[src*="attn.tv"]';
  const CREATIVE_MIN_AREA = 200 * 200;
  const CREATIVE_GROWTH_FACTOR = 3;

  // Add ?fv_signup_debug=1 (or set window.FV_SIGNUP_DEBUG) to trace the handoff.
  const DEBUG =
    window.FV_SIGNUP_DEBUG === true ||
    new URLSearchParams(window.location.search).has('fv_signup_debug');

  /**
   * @param {...unknown} args
   */
  function debug(...args) {
    if (DEBUG) console.info('[fv-footer-signup]', ...args);
  }

  /**
   * @param {HTMLFormElement} form
   * @returns {number | string | null}
   */
  function getCreativeId(form) {
    const desktop = (form.dataset.attentiveCreativeDesktop || '').trim();
    const mobile = (form.dataset.attentiveCreativeMobile || '').trim();
    const raw = window.innerWidth >= DESKTOP_MIN_WIDTH ? desktop || mobile : mobile || desktop;
    if (!raw) return null;
    const numeric = Number(raw);
    return Number.isNaN(numeric) ? raw : numeric;
  }

  /**
   * Wait for attn.js to install window.__attentive.trigger.
   * @returns {Promise<AttentiveTag | null>} the tag, or null if it never loaded
   */
  function waitForAttentive() {
    const tag = () =>
      typeof window.__attentive?.trigger === 'function' ? window.__attentive : null;

    const loaded = tag();
    if (loaded) return Promise.resolve(loaded);

    return new Promise((resolve) => {
      const startedAt = performance.now();
      const timer = setInterval(() => {
        const ready = tag();
        if (ready) {
          clearInterval(timer);
          debug(`tag ready after ${Math.round(performance.now() - startedAt)}ms`);
          resolve(ready);
          return;
        }
        if (performance.now() - startedAt >= ATTENTIVE_TIMEOUT_MS) {
          clearInterval(timer);
          resolve(null);
        }
      }, ATTENTIVE_POLL_MS);
    });
  }

  /**
   * Largest on-screen area among Attentive's containers.
   * @returns {number}
   */
  function creativeArea() {
    let largest = 0;
    for (const node of document.querySelectorAll(CREATIVE_SELECTOR)) {
      const rect = node.getBoundingClientRect();
      largest = Math.max(largest, rect.width * rect.height);
    }
    return largest;
  }

  /**
   * The trigger call resolves whether or not the unit displays, so watch the
   * creative instead: an open unit is either large outright or a big jump from
   * the idle footprint Attentive leaves on the page.
   * @param {number} baseline area before the trigger call
   * @returns {boolean}
   */
  function creativeOpened(baseline) {
    const area = creativeArea();
    if (area >= CREATIVE_MIN_AREA) return true;
    return baseline > 0 && area >= baseline * CREATIVE_GROWTH_FACTOR;
  }

  /**
   * @param {number} baseline
   * @returns {Promise<boolean>}
   */
  function waitForCreative(baseline) {
    if (creativeOpened(baseline)) return Promise.resolve(true);

    return new Promise((resolve) => {
      const startedAt = performance.now();
      const timer = setInterval(() => {
        if (creativeOpened(baseline)) {
          clearInterval(timer);
          resolve(true);
          return;
        }
        if (performance.now() - startedAt >= CREATIVE_TIMEOUT_MS) {
          clearInterval(timer);
          resolve(false);
        }
      }, CREATIVE_POLL_MS);
    });
  }

  /**
   * Hand the address to the Attentive sign-up unit.
   *
   * Fires exactly once: the unit records the address whether or not it renders a
   * creative, so retrying to force a visible popup would double-submit. Whether
   * the unit displayed is traced under DEBUG only, never treated as failure.
   *
   * @param {HTMLFormElement} form
   * @param {string} email
   * @returns {Promise<boolean>} whether the tag accepted the call
   */
  async function triggerAttentive(form, email) {
    const creativeId = getCreativeId(form);
    const attentive = await waitForAttentive();

    if (!attentive) {
      console.warn(
        '[fv-footer-signup] window.__attentive.trigger never appeared — the Attentive tag is not loaded on this page, so the address was not handed off.'
      );
      return false;
    }

    const baseline = creativeArea();

    try {
      debug('trigger(null, null, email, creativeId)', { email, creativeId });
      if (creativeId === null) {
        attentive.trigger(null, null, email);
      } else {
        attentive.trigger(null, null, email, creativeId);
      }
    } catch (error) {
      console.warn('[fv-footer-signup] Attentive trigger threw', error);
      return false;
    }

    if (DEBUG) {
      waitForCreative(baseline).then((opened) => {
        debug(
          'sign-up unit',
          opened
            ? 'displayed'
            : 'did not display — expected when the unit collects silently, check Attentive for the subscriber'
        );
      });
    }

    return true;
  }

  /**
   * Shopify answers with HTML, so the status code is the only usable signal.
   * @param {HTMLFormElement} form
   * @param {string} email
   * @returns {Promise<boolean>}
   */
  async function subscribeToShopify(form, email) {
    const body = new URLSearchParams();
    body.set('form_type', 'customer');
    body.set('utf8', '✓');
    body.set('contact[email]', email);
    body.set('contact[tags]', 'newsletter');

    try {
      const response = await fetch(form.getAttribute('action') || '/contact', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
          Accept: 'text/html,application/xhtml+xml',
        },
        body: body.toString(),
      });
      return response.ok;
    } catch (error) {
      console.warn('[fv-footer-signup] Shopify subscribe failed', error);
      return false;
    }
  }

  /**
   * @param {HTMLFormElement} form
   * @param {'success' | 'error'} state
   * @param {string} message
   */
  function setMessage(form, state, message) {
    const node = form.querySelector('[data-fv-signup-message]');
    if (!(node instanceof HTMLElement)) return;

    node.classList.toggle('fluevog-footer__signup-message--success', state === 'success');
    node.classList.toggle('fluevog-footer__signup-message--error', state === 'error');
    node.textContent = message;
    node.hidden = !message;
  }

  /**
   * @param {HTMLFormElement} form
   */
  function bind(form) {
    if (form.dataset.fvSignupBound === 'true') return;
    form.dataset.fvSignupBound = 'true';

    const input = form.querySelector('[data-fv-signup-email]');
    const button = form.querySelector('[data-fv-signup-submit]');
    const honeypot = form.querySelector('[data-fv-signup-honeypot]');

    form.addEventListener('submit', async (event) => {
      event.preventDefault();

      if (honeypot instanceof HTMLInputElement && honeypot.value) {
        debug('honeypot filled — submit ignored', honeypot.value);
        return;
      }
      if (!(input instanceof HTMLInputElement)) return;

      const email = input.value.trim();
      if (!email || !input.checkValidity()) {
        input.reportValidity();
        return;
      }

      if (button instanceof HTMLButtonElement) button.disabled = true;
      setMessage(form, 'success', '');

      const subscribed = await subscribeToShopify(form, email);
      debug('Shopify write', subscribed ? 'ok' : 'failed');

      if (button instanceof HTMLButtonElement) button.disabled = false;

      if (!subscribed) {
        setMessage(form, 'error', form.dataset.errorMessage || '');
        return;
      }

      setMessage(form, 'success', form.dataset.successMessage || '');
      input.value = '';
      const opened = await triggerAttentive(form, email);
      debug('Attentive sign-up unit', opened ? 'opened' : 'did not open');
    });
  }

  function init() {
    document.querySelectorAll('[data-fv-signup]').forEach((form) => {
      if (form instanceof HTMLFormElement) bind(form);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  document.addEventListener('shopify:section:load', init);
})();
